import React, { useState } from 'react';
import { Shield, CheckCircle, AlertTriangle, XCircle, FileText, Download, Clock, TrendingUp } from 'lucide-react';

interface ComplianceRequirement {
  id: string;
  name: string;
  status: 'approved' | 'warning' | 'missing';
  expiryDate?: string;
  category: 'insurance' | 'certification' | 'safety' | 'other';
}

interface ComplianceSupplier {
  id: string;
  name: string;
  role: string;
  overallStatus: 'compliant' | 'partial' | 'non-compliant';
  completionRate: number;
  requirements: ComplianceRequirement[];
}

interface ComplianceDashboardProps {
  suppliers: ComplianceSupplier[];
  onRequestDocuments?: (supplierId: string) => void;
  onExportPack?: (supplierId: string) => void;
  onExportAll?: () => void;
}

type FilterType = 'all' | 'compliant' | 'partial' | 'non-compliant';

export const ComplianceDashboard: React.FC<ComplianceDashboardProps> = ({
  suppliers,
  onRequestDocuments,
  onExportPack,
  onExportAll
}) => {
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [expandedSupplier, setExpandedSupplier] = useState<string | null>(null);

  // Count statuses
  const statusCounts = {
    all: suppliers.length,
    compliant: suppliers.filter(s => s.overallStatus === 'compliant').length,
    partial: suppliers.filter(s => s.overallStatus === 'partial').length,
    'non-compliant': suppliers.filter(s => s.overallStatus === 'non-compliant').length
  };

  // Calculate overall compliance
  const overallCompliance = Math.round(
    (statusCounts.compliant / suppliers.length) * 100
  );

  // Filter suppliers
  const filteredSuppliers = activeFilter === 'all'
    ? suppliers
    : suppliers.filter(s => s.overallStatus === activeFilter);

  // Sort by priority: non-compliant first, then partial, then compliant
  const sortedSuppliers = [...filteredSuppliers].sort((a, b) => {
    const priority: { [key: string]: number } = {
      'non-compliant': 0,
      'partial': 1,
      'compliant': 2
    };
    return priority[a.overallStatus] - priority[b.overallStatus];
  });

  const FilterButton: React.FC<{
    filter: FilterType;
    label: string;
    count: number;
    icon?: React.ReactNode;
    color?: string;
  }> = ({ filter, label, count, icon, color }) => {
    const isActive = activeFilter === filter;
    const hasIssue = (filter === 'non-compliant' || filter === 'partial') && count > 0;

    return (
      <button
        onClick={() => setActiveFilter(filter)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 16px',
          backgroundColor: isActive ? (color || '#f3e5f5') : 'transparent',
          border: isActive ? `1px solid ${color || '#6a1b9a'}` : '1px solid transparent',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: isActive ? 500 : 400,
          color: isActive ? 
            (filter === 'non-compliant' ? '#d93025' : 
             filter === 'partial' ? '#ea8600' : 
             filter === 'compliant' ? '#188038' : '#6a1b9a') : 
            (hasIssue && filter === 'non-compliant' ? '#d93025' : '#6b7280'),
          cursor: 'pointer',
          transition: 'all 0.2s',
          position: 'relative'
        }}
        onMouseEnter={(e) => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = '#f8f9fa';
            e.currentTarget.style.border = '1px solid #dadce0';
          }
        }}
        onMouseLeave={(e) => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.border = '1px solid transparent';
          }
        }}
      >
        {icon && <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>}
        <span>{count} {label}</span>
        {filter === 'non-compliant' && count > 0 && !isActive && (
          <span style={{
            position: 'absolute',
            top: '-2px',
            right: '-2px',
            width: '8px',
            height: '8px',
            backgroundColor: '#d93025',
            borderRadius: '50%',
            animation: 'pulse 1s infinite'
          }} />
        )}
      </button>
    );
  };

  const getCategoryIcon = (category: string) => {
    const icons: { [key: string]: string } = {
      insurance: '🛡️',
      certification: '📜',
      safety: '⚠️',
      other: '📄'
    };
    return icons[category] || '📄';
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: 'linear-gradient(135deg, #f8f9ff 0%, #f1f3ff 100%)',
      borderRadius: '16px',
      border: '1px solid #e2e8f0',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 24px 16px 24px',
        background: 'linear-gradient(135deg, #6a1b9a 0%, #8e24aa 100%)',
        color: 'white',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '12px',
            background: 'rgba(255,255,255,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px'
          }}>
            <Shield size={20} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>Compliance Dashboard</h3>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>Document management & risk mitigation</div>
          </div>
        </div>
        
        {/* Header Stats */}
        <div style={{ display: 'flex', gap: '16px', marginTop: '12px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            background: 'rgba(255,255,255,0.15)',
            borderRadius: '8px'
          }}>
            <div style={{
              fontSize: '18px',
              fontWeight: 600
            }}>{overallCompliance}%</div>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>Overall Compliance</div>
          </div>
          
          <button
            onClick={onExportAll}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontSize: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          >
            <Download size={14} />
            Export All
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
        {/* Overall Compliance Card */}
        <div style={{
        padding: '20px',
        background: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ position: 'relative' }}>
            <svg width="60" height="60">
              <circle
                cx="30"
                cy="30"
                r="25"
                stroke="#e8eaed"
                strokeWidth="5"
                fill="none"
              />
              <circle
                cx="30"
                cy="30"
                r="25"
                stroke={overallCompliance >= 80 ? '#6a1b9a' : 
                       overallCompliance >= 60 ? '#ea8600' : '#d93025'}
                strokeWidth="5"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 25}`}
                strokeDashoffset={`${2 * Math.PI * 25 * (1 - overallCompliance / 100)}`}
                transform="rotate(-90 30 30)"
                style={{ transition: 'stroke-dashoffset 0.5s ease' }}
              />
            </svg>
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#374151'
            }}>
              {overallCompliance}%
            </div>
          </div>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 600, color: '#374151' }}>
              Overall Compliance
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
              {statusCounts.compliant} of {suppliers.length} suppliers fully compliant
            </div>
            {(statusCounts['non-compliant'] > 0 || statusCounts.partial > 0) && (
              <div style={{ 
                fontSize: '13px', 
                color: statusCounts['non-compliant'] > 0 ? '#d93025' : '#ea8600',
                marginTop: '4px',
                fontWeight: 500
              }}>
                ⚠️ {statusCounts['non-compliant'] + statusCounts.partial} suppliers need attention
              </div>
            )}
          </div>
        </div>
        </div>

        {/* Filter Bar */}
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '20px',
        padding: '16px 20px',
        background: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb'
      }}>
        <FilterButton
          filter="all"
          label="All Suppliers"
          count={statusCounts.all}
          icon={<Shield className="w-4 h-4" />}
        />
        <FilterButton
          filter="compliant"
          label="Compliant"
          count={statusCounts.compliant}
          icon={<CheckCircle className="w-4 h-4" style={{ color: '#188038' }} />}
          color="#e6f4ea"
        />
        <FilterButton
          filter="partial"
          label="Expiring Soon"
          count={statusCounts.partial}
          icon={<AlertTriangle className="w-4 h-4" style={{ color: '#ea8600' }} />}
          color="#fef7e0"
        />
        <FilterButton
          filter="non-compliant"
          label="Missing Docs"
          count={statusCounts['non-compliant']}
          icon={<XCircle className="w-4 h-4" style={{ color: '#d93025' }} />}
          color="#fce8e6"
        />
        </div>

        {/* Active Filter Label */}
        {activeFilter !== 'all' && (
        <div style={{
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span style={{
            fontSize: '13px',
            color: '#5f6368',
            fontWeight: 500
          }}>
            Showing {filteredSuppliers.length} {activeFilter.replace('-', ' ')} supplier{filteredSuppliers.length !== 1 ? 's' : ''}
          </span>
          <button
            onClick={() => setActiveFilter('all')}
            style={{
              fontSize: '13px',
              color: '#1a73e8',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            Clear filter
          </button>
        </div>
        )}

        {/* Supplier Cards */}
        {sortedSuppliers.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#5f6368'
        }}>
          <div style={{ fontSize: '16px', marginBottom: '8px' }}>
            No suppliers found
          </div>
          <div style={{ fontSize: '14px' }}>
            No suppliers match the selected filter
          </div>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '16px'
        }}>
          {sortedSuppliers.map((supplier) => {
            const missingCount = supplier.requirements.filter(r => r.status === 'missing').length;
            const expiringCount = supplier.requirements.filter(r => r.status === 'warning').length;
            const isExpanded = expandedSupplier === supplier.id;

            return (
              <div
                key={supplier.id}
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  padding: '16px',
                  transition: 'box-shadow 0.2s',
                  cursor: 'pointer',
                  ...(supplier.overallStatus === 'non-compliant' && {
                    borderColor: '#d93025',
                    backgroundColor: '#fef1f0'
                  }),
                  ...(supplier.overallStatus === 'partial' && {
                    borderColor: '#ea8600',
                    backgroundColor: '#fffbf0'
                  })
                }}
                onClick={() => setExpandedSupplier(isExpanded ? null : supplier.id)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 1px 3px 1px rgba(60,64,67,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {/* Progress Circle */}
                    <div style={{ position: 'relative' }}>
                      <svg width="40" height="40">
                        <circle
                          cx="20"
                          cy="20"
                          r="16"
                          stroke="#e8eaed"
                          strokeWidth="3"
                          fill="none"
                        />
                        <circle
                          cx="20"
                          cy="20"
                          r="16"
                          stroke={supplier.overallStatus === 'compliant' ? '#188038' :
                                 supplier.overallStatus === 'partial' ? '#ea8600' : '#d93025'}
                          strokeWidth="3"
                          fill="none"
                          strokeDasharray={`${2 * Math.PI * 16}`}
                          strokeDashoffset={`${2 * Math.PI * 16 * (1 - supplier.completionRate / 100)}`}
                          transform="rotate(-90 20 20)"
                        />
                      </svg>
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: '#202124'
                      }}>
                        {supplier.completionRate}%
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: 500, color: '#202124' }}>
                        {supplier.name}
                      </div>
                      <div style={{ fontSize: '14px', color: '#5f6368' }}>
                        {supplier.role}
                      </div>
                    </div>
                  </div>
                  {supplier.overallStatus === 'compliant' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : supplier.overallStatus === 'partial' ? (
                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>

                {/* Status Summary */}
                <div style={{
                  fontSize: '13px',
                  color: '#5f6368',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  {supplier.overallStatus === 'compliant' && (
                    <span style={{ color: '#188038', fontWeight: 500 }}>
                      ✓ All documents approved
                    </span>
                  )}
                  {missingCount > 0 && (
                    <span style={{ color: '#d93025', fontWeight: 500 }}>
                      {missingCount} missing
                    </span>
                  )}
                  {expiringCount > 0 && (
                    <span style={{ color: '#ea8600', fontWeight: 500 }}>
                      {expiringCount} expiring
                    </span>
                  )}
                </div>

                {/* Document Icons */}
                <div style={{
                  display: 'flex',
                  gap: '6px',
                  marginBottom: '12px'
                }}>
                  {supplier.requirements.slice(0, 5).map((req) => (
                    <div
                      key={req.id}
                      title={req.name}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        backgroundColor: req.status === 'approved' ? '#e6f4ea' :
                                       req.status === 'warning' ? '#fef7e0' : '#fce8e6',
                        border: `1px solid ${req.status === 'approved' ? '#ceead6' :
                                            req.status === 'warning' ? '#fde293' : '#f8c9c6'}`
                      }}
                    >
                      {getCategoryIcon(req.category)}
                    </div>
                  ))}
                  {supplier.requirements.length > 5 && (
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e8eaed',
                      color: '#5f6368',
                      fontWeight: 500
                    }}>
                      +{supplier.requirements.length - 5}
                    </div>
                  )}
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div style={{
                    borderTop: '1px solid #e8eaed',
                    marginTop: '12px',
                    paddingTop: '12px'
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: 500, color: '#5f6368', marginBottom: '8px' }}>
                      Document Details
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {supplier.requirements.map((req) => (
                        <div
                          key={req.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            fontSize: '13px',
                            padding: '4px 0'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>{getCategoryIcon(req.category)}</span>
                            <span style={{ color: '#202124' }}>{req.name}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {req.status === 'approved' && (
                              <CheckCircle className="w-3 h-3 text-green-600" />
                            )}
                            {req.status === 'warning' && (
                              <>
                                <Clock className="w-3 h-3 text-yellow-600" />
                                <span style={{ fontSize: '12px', color: '#ea8600' }}>
                                  {req.expiryDate}
                                </span>
                              </>
                            )}
                            {req.status === 'missing' && (
                              <span style={{ fontSize: '12px', color: '#d93025', fontWeight: 500 }}>
                                Required
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  marginTop: '12px'
                }}>
                  {missingCount > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRequestDocuments?.(supplier.id);
                      }}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        backgroundColor: '#d93025',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#b7241c';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#d93025';
                      }}
                    >
                      <FileText className="w-4 h-4" />
                      Request Docs
                    </button>
                  )}
                  {supplier.overallStatus === 'compliant' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onExportPack?.(supplier.id);
                      }}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        backgroundColor: '#fff',
                        color: '#1a73e8',
                        border: '1px solid #dadce0',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                        e.currentTarget.style.borderColor = '#1a73e8';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fff';
                        e.currentTarget.style.borderColor = '#dadce0';
                      }}
                    >
                      <Download className="w-4 h-4" />
                      Export Pack
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
};